import React, { createContext, useContext, useEffect, useState } from 'react';

const DropshipperContext = createContext(null);

const COOKIE_DR = 'gf_dr';
const COOKIE_VISITOR = 'gf_visitor_id';
const COOKIE_DR_MAX_AGE = 60 * 60 * 24 * 30; // 30 days
const COOKIE_VISITOR_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'development' ? 'http://localhost:4001' : 'https://api.birkshoes.store'
);

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function writeCookie(name, value, maxAgeSeconds) {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${secure}`;
}

function generateUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const useDropshipper = () => {
  const ctx = useContext(DropshipperContext);
  if (!ctx) {
    throw new Error('useDropshipper must be used inside <DropshipperProvider>');
  }
  return ctx;
};

export const DropshipperProvider = ({ children }) => {
  const [slug, setSlug] = useState(null);
  const [visitorId, setVisitorId] = useState(null);
  const [pixelConfig, setPixelConfig] = useState({ metaPixelId: null, tiktokPixelId: null });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let activeSlug = null;
    let urlSlug = null;

    try {
      const params = new URLSearchParams(window.location.search);
      urlSlug = params.get('dr');
    } catch (e) {
      urlSlug = null;
    }

    const cookieSlug = readCookie(COOKIE_DR);

    if (urlSlug) {
      activeSlug = urlSlug.toLowerCase().trim();
      writeCookie(COOKIE_DR, activeSlug, COOKIE_DR_MAX_AGE);
    } else if (cookieSlug) {
      activeSlug = cookieSlug;
    }

    let vid = readCookie(COOKIE_VISITOR);
    if (!vid) {
      vid = generateUuid();
      writeCookie(COOKIE_VISITOR, vid, COOKIE_VISITOR_MAX_AGE);
    }
    setVisitorId(vid);
    setSlug(activeSlug);

    if (!activeSlug) {
      setIsLoading(false);
      return;
    }

    if (urlSlug) {
      fetch(`${API_BASE_URL}/api/attribution/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dr: activeSlug,
          visitorId: vid,
          landingUrl: window.location.href,
          referrer: document.referrer || null,
        }),
      }).catch(() => {});
    }

    fetch(`${API_BASE_URL}/api/catalog/dropshipper-pixels?dr=${encodeURIComponent(activeSlug)}`)
      .then((r) => r.json())
      .then((data) => {
        if (data && data.dropshipper) {
          setPixelConfig({
            metaPixelId: data.dropshipper.metaPixelId || null,
            tiktokPixelId: data.dropshipper.tiktokPixelId || null,
          });
        } else {
          // Slug invalid or inactive: clear cookie so we don't keep retrying
          writeCookie(COOKIE_DR, '', 0);
          setSlug(null);
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <DropshipperContext.Provider
      value={{
        slug,
        visitorId,
        metaPixelId: pixelConfig.metaPixelId,
        tiktokPixelId: pixelConfig.tiktokPixelId,
        isLoading,
      }}
    >
      {children}
    </DropshipperContext.Provider>
  );
};
